import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ChartHorizontalBar, ChartSerie } from './components/chart-horizontal-bar/chart-horizontal-bar';
import { ChartVerticalBar } from './components/chart-vertical-bar/chart-vertical-bar';
import { SeriesToggle, ToggleSerie } from './components/series-toggle/series-toggle';
import { AsistenciaService } from '../../../../services/asistencia/asistencia.service';
import { ReporteInstitucion, ReporteNivel, ReporteGrado } from '../../../../core/models/asistencia/asistencia.model';
import { ApiResponse } from '../../../../core/models/api-response.model';

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface RawAttendance {
    label: string;
    asistio: number;
    tarde: number;
    tarde_justificada: number;
    falta: number;
    ausencia_justificada: number;
}

interface GradeGroup {
    grado: string;    // "1er Grado"
    ordinal: string;  // "1°"
    secciones: RawAttendance[];
}

interface NormalizedChartData {
    categories: string[];
    series: ChartSerie[];
}

interface KPI {
    label: string;
    value: string;
    delta: string;
    icon: string;
    accent: string;
}

// ─── Series Config ────────────────────────────────────────────────────────────
// Orden: de "mejor" a "peor" → importante para el stacked (verde primero)

const SERIES_CONFIG: Array<{
    id: keyof Omit<RawAttendance, 'label'>;
    label: string;
    color: string;
    isDefault: boolean;
}> = [
        { id: 'asistio', label: 'Asistió', color: '#10B981', isDefault: true },
        { id: 'tarde', label: 'Tardanza', color: '#F59E0B', isDefault: true },
        { id: 'tarde_justificada', label: 'Tardanza Just.', color: '#3B82F6', isDefault: false },
        { id: 'falta', label: 'Faltó', color: '#EF4444', isDefault: true },
        { id: 'ausencia_justificada', label: 'Falta Just.', color: '#8B5CF6', isDefault: false },
    ];



// ─── Normalize ───────────────────────────────────────────────────────────────

function normalizeRow(
    row: RawAttendance,
    seriesIds: string[],
): Record<string, number> {
    const total = seriesIds.reduce((s, id) => s + ((row as any)[id] ?? 0), 0);
    if (total === 0) return Object.fromEntries(seriesIds.map(id => [id, 0]));
    return Object.fromEntries(
        seriesIds.map(id => [id, Math.round(((row as any)[id] ?? 0) / total * 100)]),
    );
}

function toChartData(rows: RawAttendance[]): NormalizedChartData {
    const ids = SERIES_CONFIG.map(s => s.id as string);
    return {
        categories: rows.map(r => r.label),
        series: SERIES_CONFIG.map(cfg => ({
            id: cfg.id,
            label: cfg.label,
            color: cfg.color,
            data: rows.map(row => normalizeRow(row, ids)[cfg.id] ?? 0),
            rawData: rows.map(row => (row as any)[cfg.id] ?? 0),
        })),
    };
}

/** Suma todas las secciones de un grado → 1 fila con el label del grado */
function aggregateGrade(group: GradeGroup): RawAttendance {
    const ids = SERIES_CONFIG.map(s => s.id as string);
    const summed: any = { label: group.grado };
    for (const id of ids) {
        summed[id] = group.secciones.reduce((acc, s) => acc + ((s as any)[id] ?? 0), 0);
    }
    return summed as RawAttendance;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface NivelTab {
    id: string;
    levelId: number;
    label: string;
    icon: string;
}

@Component({
    selector: 'app-dashboard-admin',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatNativeDateModule,
        ChartHorizontalBar,
        ChartVerticalBar,
        SeriesToggle
    ],
    providers: [DatePipe],
    templateUrl: './dashboard-admin.html',
    styleUrl: './dashboard-admin.scss',
})

export class DashboardAdmin implements OnInit {

    private asistenciaService = inject(AsistenciaService);
    private datePipe = inject(DatePipe);
    private cdr = inject(ChangeDetectorRef);

    today = new Date();

    dateRange = new FormGroup({
        start: new FormControl<Date | null>(this.today),
        end: new FormControl<Date | null>(this.today),
    });

    toggleSeries: ToggleSerie[] = SERIES_CONFIG.map(s => ({
        id: s.id, label: s.label, color: s.color, isDefault: s.isDefault,
    }));

    defaultActive = SERIES_CONFIG.filter(s => s.isDefault).map(s => s.id);

    activeGeneral: string[] = [...this.defaultActive];
    activeNiveles: string[] = [...this.defaultActive];
    activeGradesTotals: string[] = [...this.defaultActive];
    activeByGrade: Record<string, string[]> = {};

    chartGeneral: NormalizedChartData = this.emptyChartData();
    chartNiveles: NormalizedChartData = this.emptyChartData();
    chartGradesTotals: NormalizedChartData = this.emptyChartData();
    chartsByGrade: Array<{ group: GradeGroup; data: NormalizedChartData }> = [];

    kpis: KPI[] = this.emptyKpis();

    // ─── Tabs de nivel educativo ──────────────────────────────────────────────
    nivelTabs: NivelTab[] = [];
    activeNivelTab = '';
    get activeNivelLabel(): string {
        return this.nivelTabs.find(t => t.id === this.activeNivelTab)?.label ?? '';
    }

    ngOnInit(): void {
        this.loadApiData();
    }

    onDateRangeChange(): void {
        if (this.dateRange.valid && this.dateRange.value.start && this.dateRange.value.end) {
            this.loadApiData();
        }
    }

    private loadApiData(): void {
        const start = this.dateRange.value.start;
        const end = this.dateRange.value.end;
        if (!start || !end) return;

        const startStr = this.datePipe.transform(start, 'yyyy-MM-dd') || '';
        const endStr = this.datePipe.transform(end, 'yyyy-MM-dd') || '';

        this.asistenciaService.getReporteInstitucion(startStr, endStr).subscribe({
            next: (res: ApiResponse<ReporteInstitucion[]>) => {
                if (res.success && res.data) {
                    this.processApiData(res.data);
                    this.cdr.detectChanges();
                }
            },
            error: (err: any) => {
                console.error('[Dashboard] Error institucion:', err);
            }
        });
    }


    private processApiData(data: ReporteInstitucion[]): void {
        // Build MOCK_GENERAL alternative and update charts
        let totalAsistio = 0, totalTarde = 0, totalTardeJ = 0, totalFalta = 0, totalFaltaJ = 0;

        // Arrays for charts
        const nivelRows: RawAttendance[] = [];

        // Group by Nivel -> Grado -> Secciones as needed.
        // Assuming the endpoint returns a flat array with fields `nivel`, `gradoId`, etc.
        // For simplicity, let's aggregate general for now.
        data.forEach(item => {
            totalAsistio += item.puntual;
            totalTarde += item.tarde;
            totalTardeJ += item.tardeJustificada;
            totalFalta += item.falta;
            totalFaltaJ += item.faltaJustificada;
        });

        const generalRow: RawAttendance = {
            label: 'Institución',
            asistio: totalAsistio,
            tarde: totalTarde,
            tarde_justificada: totalTardeJ,
            falta: totalFalta,
            ausencia_justificada: totalFaltaJ
        };

        this.chartGeneral = toChartData([generalRow]);

        // Group by Nivel
        const nivelesMap = new Map<number, RawAttendance>();
        const dynamicTabs: NivelTab[] = [];

        data.forEach(item => {
            if (!nivelesMap.has(item.nivelId)) {
                const label = item.nivel || 'Desconocido';
                nivelesMap.set(item.nivelId, {
                    label: label,
                    asistio: 0, tarde: 0, tarde_justificada: 0, falta: 0, ausencia_justificada: 0
                });

                let icon = 'heroicons_solid:book-open';
                const idStr = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
                if (idStr.includes('inicial')) icon = 'heroicons_solid:academic-cap';
                else if (idStr.includes('secundaria')) icon = 'heroicons_solid:building-library';

                dynamicTabs.push({ id: idStr, levelId: item.nivelId, label: label, icon });
            }
            const nn = nivelesMap.get(item.nivelId)!;
            nn.asistio += item.puntual;
            nn.tarde += item.tarde;
            nn.tarde_justificada += item.tardeJustificada;
            nn.falta += item.falta;
            nn.ausencia_justificada += item.faltaJustificada;
        });

        this.chartNiveles = toChartData(Array.from(nivelesMap.values()));

        this.nivelTabs = dynamicTabs.sort((a, b) => a.levelId - b.levelId);

        if (!this.nivelTabs.find(t => t.id === this.activeNivelTab) && this.nivelTabs.length > 0) {
            this.activeNivelTab = this.nivelTabs[0].id;
        }

        // Re-build KPIs with the new generic data
        this.buildKpis(generalRow);

        // Trigger load for the valid tab level
        this.loadLevelApiData(this.activeNivelTab);
    }

    selectNivelTab(nivelId: string): void {
        if (this.activeNivelTab === nivelId) return;
        this.activeNivelTab = nivelId;
        this.loadLevelApiData(nivelId);
    }

    private getLevelId(tabId: string): number {
        const tab = this.nivelTabs.find(t => t.id === tabId);
        return tab ? tab.levelId : 2;
    }

    private loadLevelApiData(nivelTabId: string): void {
        const start = this.dateRange.value.start;
        const end = this.dateRange.value.end;
        if (!start || !end) {
            this.clearLevelData();
            return;
        }

        const startStr = this.datePipe.transform(start, 'yyyy-MM-dd') || '';
        const endStr = this.datePipe.transform(end, 'yyyy-MM-dd') || '';
        const levelId = this.getLevelId(nivelTabId);

        this.asistenciaService.getReporteNivel(startStr, endStr, levelId).subscribe({
            next: (res: ApiResponse<ReporteNivel[]>) => {
                if (res.success && res.data) {
                    this.processLevelApiData(res.data, nivelTabId, startStr, endStr);
                    this.cdr.detectChanges();
                } else {
                    this.clearLevelData();
                    this.cdr.detectChanges();
                }
            },
            error: (err: any) => {
                console.error('Error al obtener reporte por nivel:', err);
                this.clearLevelData();
                this.cdr.detectChanges();
            }
        });
    }

    private processLevelApiData(data: ReporteNivel[], nivelTabId: string, startStr: string, endStr: string): void {
        // Build rows for chartGradesTotals
        const gradeTotalRows: RawAttendance[] = data.map(item => ({
            label: item.grado || 'Desconocido',
            asistio: item.puntual,
            tarde: item.tarde,
            tarde_justificada: item.tardeJustificada,
            falta: item.falta,
            ausencia_justificada: item.faltaJustificada
        }));

        this.activeGradesTotals = [...this.defaultActive];
        this.chartGradesTotals = toChartData(gradeTotalRows);

        if (data.length === 0) {
            this.chartsByGrade = [];
            this.activeByGrade = {};
            return;
        }

        const sectionRequests: Observable<{ grado: string, ordinal: string, res: ApiResponse<ReporteGrado[]> }>[] = data.map(item => {
            return this.asistenciaService.getReporteGrado(startStr, endStr, item.gradoId).pipe(
                map(res => ({ grado: item.grado, ordinal: this.getOrdinal(item.grado), res })),
                catchError(() => of({ grado: item.grado, ordinal: this.getOrdinal(item.grado), res: { success: 0, message: '', data: [] as ReporteGrado[] } as unknown as ApiResponse<ReporteGrado[]> }))
            );
        });

        forkJoin(sectionRequests).subscribe(results => {
            this.activeByGrade = {};
            this.chartsByGrade = results
                .filter(r => r.res.success && r.res.data)
                .map(r => {
                    const sortedSections = r.res.data!.sort((a, b) => (a.seccion || '').localeCompare(b.seccion || ''));
                    const secciones: RawAttendance[] = sortedSections.map(sec => ({
                        label: `Sec. ${sec.seccion}`,
                        asistio: sec.puntual,
                        tarde: sec.tarde,
                        tarde_justificada: sec.tardeJustificada,
                        falta: sec.falta,
                        ausencia_justificada: sec.faltaJustificada
                    }));

                    this.activeByGrade[r.grado] = [...this.defaultActive];

                    return {
                        group: { grado: r.grado, ordinal: r.ordinal, secciones },
                        data: toChartData(secciones)
                    };
                });
            this.cdr.detectChanges();
        });
    }

    private getOrdinal(grado: string): string {
        const up = (grado || '').toUpperCase();
        if (up.includes('PRIMER') || up.includes('1')) return '1°';
        if (up.includes('SEGUND') || up.includes('2')) return '2°';
        if (up.includes('TERCER') || up.includes('3')) return '3°';
        if (up.includes('CUART') || up.includes('4')) return '4°';
        if (up.includes('QUINT') || up.includes('5')) return '5°';
        if (up.includes('SEXT') || up.includes('6')) return '6°';
        return '°';
    }

    private emptyChartData(): NormalizedChartData {
        return {
            categories: [],
            series: SERIES_CONFIG.map(cfg => ({
                id: cfg.id as string,
                label: cfg.label,
                color: cfg.color,
                data: [],
                rawData: [],
            }))
        };
    }

    private emptyKpis(): KPI[] {
        return [
            { label: 'Total Registrados', value: '0', delta: 'alumnos hoy', icon: 'heroicons_solid:users', accent: '#6366f1' },
            { label: 'Asistieron', value: '0%', delta: '0 alumnos', icon: 'heroicons_solid:check-circle', accent: '#10b981' },
            { label: 'Tardanzas', value: '0%', delta: '0 alumnos', icon: 'heroicons_solid:clock', accent: '#f59e0b' },
            { label: 'Faltas', value: '0%', delta: '0 alumnos', icon: 'heroicons_solid:x-circle', accent: '#ef4444' },
        ];
    }

    private clearLevelData(): void {
        this.activeGradesTotals = [...this.defaultActive];
        this.chartGradesTotals = this.emptyChartData();
        this.activeByGrade = {};
        this.chartsByGrade = [];
    }

    private buildKpis(g: RawAttendance): void {
        const ids = SERIES_CONFIG.map(s => s.id as string);
        const total = ids.reduce((s, id) => s + ((g as any)[id] ?? 0), 0);
        const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) + '%' : '—';

        this.kpis = [
            {
                label: 'Total Registrados',
                value: total.toLocaleString(),
                delta: 'alumnos hoy',
                icon: 'heroicons_solid:users',
                accent: '#6366f1',
            },
            {
                label: 'Asistieron',
                value: pct(g.asistio),
                delta: `${g.asistio} alumnos`,
                icon: 'heroicons_solid:check-circle',
                accent: '#10b981',
            },
            {
                label: 'Tardanzas',
                value: pct(g.tarde + g.tarde_justificada),
                delta: `${g.tarde + g.tarde_justificada} alumnos`,
                icon: 'heroicons_solid:clock',
                accent: '#f59e0b',
            },
            {
                label: 'Faltas',
                value: pct(g.falta + g.ausencia_justificada),
                delta: `${g.falta + g.ausencia_justificada} alumnos`,
                icon: 'heroicons_solid:x-circle',
                accent: '#ef4444',
            },
        ];
    }
}
